import { LoginState } from "@propelauth/frontend-apis";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import {
  useRedirectFunctions,
  withAuthInfo,
  WithAuthInfoProps,
} from "@propelauth/react";
import { Button } from "components/ui/button";
import RootlyFullLogo from "components/ui/icons/rootly_logo";
import TierZeroMark from "components/ui/icons/tierzero_mark";
import { Input } from "components/ui/input";
import React from "react";
import { useMockPropelAuth } from "src/common/config";
import AwsMarketplacePage from "src/pages/AwsMarketplacePage";
import LoginPage from "src/pages/LoginPage";
import RegistrationCompletePage from "src/pages/RegistrationCompletePage";
import VerifyMfaPage from "src/pages/VerifyMfaPage";
import {
  getBrandThemeFromDomain,
  getProductName,
  getSupportEmail,
  isRootlyBranded,
  isRootlyDomain,
} from "src/utils/brandTheme";

const ROOTLY_AUTH_RETRY_KEY = "tz_rootly_auth_retry";
export const MAGIC_LINK_PENDING_KEY = "tz_magic_link_pending";
const AUTH_RELOAD_KEY = "tz_auth_reload_count";
const MAX_AUTH_RELOADS = 2;
export const POST_LOGIN_REDIRECT_KEY = "tz_post_login_redirect";

type PendingOrgType = { id: string; name: string };

const JoinOrgPage = React.memo<{
  pendingOrg: PendingOrgType;
  onConfirm: (firstName: string, lastName: string) => Promise<void>;
}>(function JoinOrgPage({ pendingOrg, onConfirm }) {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const brandTheme = getBrandThemeFromDomain();
  const productName = getProductName(brandTheme);
  const supportEmail = getSupportEmail(brandTheme);
  const isRootly = isRootlyBranded(brandTheme);

  const handleSubmit = React.useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) {
      setError("Please enter your first and last name.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onConfirm(first, last);
    } catch {
      setError(`Something went wrong. Please reach out to ${supportEmail}.`);
      setSubmitting(false);
    }
  }, [firstName, lastName, onConfirm, supportEmail]);

  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          {isRootly ? (
            <RootlyFullLogo className="h-10 w-auto" />
          ) : (
            <TierZeroMark className="h-14 w-auto text-[#3B82F6]" />
          )}
        </div>

        <h1 className="text-2xl font-bold mb-2 text-center">
          Welcome to {productName}
        </h1>
        <p className="text-neutral-400 text-center mb-8">
          You're joining <span className="text-white font-medium">{pendingOrg.name}</span>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="rounded-xl border border-neutral-700/60 bg-neutral-900/50 p-6 space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm text-neutral-400 mb-1.5">
                First name
              </label>
              <Input
                id="firstName"
                type="text"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setError(""); }}
                className="w-full bg-neutral-800/80 border-neutral-700 text-white placeholder:text-neutral-500 h-12 text-base"
                autoFocus
                autoComplete="given-name"
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm text-neutral-400 mb-1.5">
                Last name
              </label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setError(""); }}
                className="w-full bg-neutral-800/80 border-neutral-700 text-white placeholder:text-neutral-500 h-12 text-base"
                autoComplete="family-name"
                disabled={submitting}
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full mt-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium"
            loading={submitting}
            disabled={submitting}
          >
            Continue
          </Button>
        </form>
      </div>
    </div>
  );
});

const BrandedSplash: React.FC<{ title: React.ReactNode; body: React.ReactNode }> = ({ title, body }) => {
  const brandTheme = getBrandThemeFromDomain();
  const isRootly = isRootlyBranded(brandTheme);
  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg text-center">
        <div className="flex justify-center mb-6">
          {isRootly ? (
            <RootlyFullLogo className="h-10 w-auto" />
          ) : (
            <TierZeroMark className="h-14 w-auto text-[#3B82F6]" />
          )}
        </div>
        <h1 className="text-2xl font-bold mb-4">{title}</h1>
        <p className="text-neutral-400">{body}</p>
      </div>
    </div>
  );
};

const HomePage = withAuthInfo((props: WithAuthInfoProps) => {
  const { redirectToLoginPage } = useRedirectFunctions();
  const { fetchLoginState, updateUserMetadata, fetchJoinableOrgs, joinOrg } = useAuthFrontendApis();
  const [rootlyRetrying, setRootlyRetrying] = React.useState(false);
  const [checkingLoginState, setCheckingLoginState] = React.useState(false);
  const [pendingOrg, setPendingOrg] = React.useState<PendingOrgType | null>(null);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [unsupportedBrowser, setUnsupportedBrowser] = React.useState(false);
  const [mfaRequired, setMfaRequired] = React.useState(false);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = React.useState(false);

  // Check URL params for special flows
  const urlParams = new URLSearchParams(window.location.search);
  const isAwsInstall = urlParams.get("aws_install") === "true";
  const isRegistrationComplete = urlParams.get("registration_complete") === "true";
  const fromExtension = urlParams.get("from_extension") === "true";

  // Check if we're on the Rootly domain (before auth)
  const onRootlyDomain = isRootlyDomain();

  // Clean up stale ?rt= params left by previous redirect loops
  React.useEffect(() => {
    if (urlParams.has("rt") && !isAwsInstall && !isRegistrationComplete && !fromExtension) {
      urlParams.delete("rt");
      const clean = urlParams.toString();
      const newUrl = window.location.pathname + (clean ? `?${clean}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // On Rootly domain, browsers that block third-party cookies may report
  // unauthenticated on the first load after magic link login, but succeed
  // on a subsequent load. Reload once before giving up and showing login.
  React.useEffect(() => {
    if (props.isLoggedIn || isAwsInstall || isRegistrationComplete) {
      try { sessionStorage.removeItem(AUTH_RELOAD_KEY); } catch { /* ignore */ }
      return;
    }
    if (onRootlyDomain) {
      try {
        const retryTimestamp = sessionStorage.getItem(ROOTLY_AUTH_RETRY_KEY);
        const now = Date.now();

        if (!retryTimestamp || (now - parseInt(retryTimestamp, 10) > 30000)) {
          setRootlyRetrying(true);
          sessionStorage.setItem(ROOTLY_AUTH_RETRY_KEY, now.toString());
          const timeout = setTimeout(() => window.location.reload(), 2000);
          return () => clearTimeout(timeout);
        }
        sessionStorage.removeItem(ROOTLY_AUTH_RETRY_KEY);
        // Route every logged-out visitor through Rootly's SSO redirect endpoint.
        // For deep links (e.g. /investigations/inv-123) we pass the pathname as
        // `redirect_to` so the user lands on the intended page after authenticating;
        // for bare root visits we omit the param and let Rootly's endpoint default
        // to /chat. This only works because the in-app logout flow for Rootly
        // managed orgs is hidden — otherwise an intentional logout would be
        // instantly re-authenticated by Rootly SSO.
        //
        // Only forward "simple" same-origin paths as `redirect_to`: must start
        // with a single "/" and not "//" or "/\", which a downstream URL parser
        // could treat as a protocol-relative URL and redirect cross-origin. This
        // is defense in depth — Rootly's endpoint presumably validates redirect_to,
        // but these URLs travel through Slack/email where attackers craft lookalikes.
        const pathname = window.location.pathname;
        const isSafeDeepLink =
          pathname !== "/" &&
          pathname.startsWith("/") &&
          !pathname.startsWith("//") &&
          !pathname.startsWith("/\\");
        window.location.href = isSafeDeepLink
          ? `https://rootly.com/account/ai-sre/redirect?redirect_to=${encodeURIComponent(pathname)}`
          : "https://rootly.com/account/ai-sre/redirect";
        return;
      } catch {
        // sessionStorage blocked (SecurityError) — fall through to login page
      }
    }

    // In dev mode with mock PropelAuth, redirect directly (auto-authenticates)
    if (useMockPropelAuth) {
      redirectToLoginPage({ postLoginRedirectUrl: window.location.href });
      return undefined;
    }

    let hasMagicLinkPending = false;
    try {
      const pendingTimestamp = localStorage.getItem(MAGIC_LINK_PENDING_KEY);
      hasMagicLinkPending = !!pendingTimestamp && Date.now() - Number(pendingTimestamp) <= 3600000;
      if (!hasMagicLinkPending) localStorage.removeItem(MAGIC_LINK_PENDING_KEY);
    } catch { /* ignore */ }

    let cancelled = false;
    if (hasMagicLinkPending) setCheckingLoginState(true);

    fetchLoginState().then(async (response) => {
      await response.handle({
        async success(data) {
          if (cancelled) return;

          console.warn("[Auth] Login state:", data.login_state);

          if (data.login_state === LoginState.LOGIN_REQUIRED) {
            try { sessionStorage.removeItem(AUTH_RELOAD_KEY); } catch { /* ignore */ }
            return;
          }

          try { localStorage.removeItem(MAGIC_LINK_PENDING_KEY); } catch { /* ignore */ }

          if (data.login_state === LoginState.USER_MISSING_REQUIRED_PROPERTIES) {
            // Auto-fill required properties — name will be collected in the org join step
            try {
              const count = parseInt(sessionStorage.getItem(AUTH_RELOAD_KEY) || "0", 10);
              if (count >= MAX_AUTH_RELOADS) {
                setUnsupportedBrowser(true);
                return;
              }
            } catch { /* ignore */ }
            await updateUserMetadata({ first_name: "-", last_name: "-" }).then(async (metaResponse) => {
              await metaResponse.handle({
                async success() {
                  if (cancelled) return;
                  await props.refreshAuthInfo();
                  await new Promise(resolve => setTimeout(resolve, 200));
                  if (!cancelled) {
                    try {
                      const count = parseInt(sessionStorage.getItem(AUTH_RELOAD_KEY) || "0", 10);
                      sessionStorage.setItem(AUTH_RELOAD_KEY, (count + 1).toString());
                    } catch { /* ignore */ }
                    window.location.reload();
                  }
                },
                unexpectedOrUnhandled() {},
              });
            }).catch(() => {});
            return;
          }

          if (data.login_state === LoginState.USER_MUST_BE_IN_AT_LEAST_ONE_ORG) {
            try {
              const orgsResponse = await fetchJoinableOrgs();
              await orgsResponse.handle({
                success(orgsData) {
                  if (cancelled) return;
                  if (orgsData.orgs.length > 0) {
                    setPendingOrg({ id: orgsData.orgs[0].id, name: orgsData.orgs[0].name });
                  } else {
                    setAuthError("Your organization has not been enabled yet.");
                  }
                },
                unexpectedOrUnhandled() {},
              });
            } catch { /* ignore */ }
            return;
          }

          if (data.login_state === LoginState.LOGGED_IN) {
            // PropelAuth session exists but AuthProvider didn't detect it.
            // Try refreshAuthInfo first, then fall back to reload (as PropelAuth recommends).
            try {
              await props.refreshAuthInfo();
            } catch { /* ignore */ }

            // Yield to let React process the state update from refreshAuthInfo.
            // If isLoggedIn flipped, the effect cleanup sets cancelled=true.
            await new Promise(resolve => setTimeout(resolve, 200));

            // If refreshAuthInfo didn't flip isLoggedIn, reload with a counter guard.
            if (!cancelled) {
              try {
                const count = parseInt(sessionStorage.getItem(AUTH_RELOAD_KEY) || "0", 10);
                if (count < MAX_AUTH_RELOADS) {
                  sessionStorage.setItem(AUTH_RELOAD_KEY, (count + 1).toString());
                  window.location.reload();
                } else {
                  setUnsupportedBrowser(true);
                }
              } catch { /* ignore */ }
            }
            return;
          }

          if (data.login_state === LoginState.TWO_FACTOR_REQUIRED) {
            try { sessionStorage.removeItem(AUTH_RELOAD_KEY); } catch { /* ignore */ }
            setMfaRequired(true);
            return;
          }

          if (data.login_state === LoginState.TWO_FACTOR_ENROLLMENT_REQUIRED) {
            setMfaEnrollmentRequired(true);
            return;
          }

          // Other intermediate states (EMAIL_NOT_CONFIRMED_YET, UPDATE_PASSWORD_REQUIRED, etc.)
          console.warn("[Auth] Intermediate login state:", data.login_state);
        },
        unexpectedOrUnhandled() {},
      });
    }).catch(() => {}).finally(() => {
      if (!cancelled) setCheckingLoginState(false);
    });

    return () => { cancelled = true; };
  }, [props.isLoggedIn, isAwsInstall, isRegistrationComplete, onRootlyDomain, redirectToLoginPage, fetchLoginState, updateUserMetadata, fetchJoinableOrgs, joinOrg, props.refreshAuthInfo]);

  // Save the current URL so we can redirect back after auth (uses localStorage
  // so it survives cross-tab magic link clicks, with a 1-hour TTL)
  React.useEffect(() => {
    if (props.isLoggedIn) return;
    try {
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      if (currentPath !== "/") {
        localStorage.setItem(POST_LOGIN_REDIRECT_KEY, JSON.stringify({ path: currentPath, ts: Date.now() }));
      }
    } catch { /* storage blocked */ }
  }, [props.isLoggedIn]);

  const handleJoinOrg = React.useCallback(async (firstName: string, lastName: string) => {
    if (!pendingOrg) return;

    // Update user metadata with their name
    const metaResponse = await updateUserMetadata({ first_name: firstName, last_name: lastName });
    await metaResponse.handle({
      async success() {
        // Join the org
        const joinResponse = await joinOrg(pendingOrg.id);
        await joinResponse.handle({
          async success() {
            window.location.reload();
          },
          unexpectedOrUnhandled() {
            throw new Error("Failed to join org");
          },
        });
      },
      unexpectedOrUnhandled() {
        throw new Error("Failed to update name");
      },
    });
  }, [pendingOrg, updateUserMetadata, joinOrg]);

  // Special case: AWS Marketplace registration flow
  if (isAwsInstall) {
    return <AwsMarketplacePage />;
  }

  // Special case: Registration complete
  if (isRegistrationComplete) {
    return <RegistrationCompletePage />;
  }

  // Check if user came from the Chrome extension and is now logged in
  if (props.isLoggedIn && fromExtension) {
    const productName = getProductName(getBrandThemeFromDomain());
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-midnight to-dark-teal text-white">
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="bg-steel/30 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-steel/50">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan to-[#56A64B]">
                Authentication Successful!
              </h1>
            </div>
            <p className="text-lg text-muted-foreground mb-6">
              You're now logged in to {productName}.
            </p>
            <p className="text-md text-muted-foreground mb-8">
              Return to your Chrome extension and click the {productName} icon to start teaching dashboards.
            </p>
            <p className="text-sm text-slate-500">
              You can close this tab.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already logged in — app.tsx will handle rendering the authenticated app
  if (props.isLoggedIn) {
    return null;
  }

  // Auth loop exhausted — cookies blocked by browser, show splash
  if (unsupportedBrowser) {
    return <BrandedSplash title="Browser not supported" body="Please try again in Chrome or Firefox." />;
  }

  // While Rootly domain is retrying auth, show nothing (will reload)
  if (rootlyRetrying) {
    return null;
  }

  // While checking login state via PropelAuth frontend-apis, show nothing
  if (checkingLoginState) {
    return null;
  }

  // In mock PropelAuth mode, the useEffect above will redirect — show nothing
  if (useMockPropelAuth) {
    return null;
  }

  // 2FA enrollment required — we don't have an enrollment UI yet, so direct to support
  if (mfaEnrollmentRequired) {
    const supportEmail = getSupportEmail(getBrandThemeFromDomain());
    return (
      <BrandedSplash
        title="Two-factor authentication setup required"
        body={
          <>
            Your organization requires two-factor authentication, but enrollment isn't yet
            available in this UI. Please reach out to{" "}
            <a href={`mailto:${supportEmail}`} className="text-[#3B82F6] underline underline-offset-2">{supportEmail}</a>{" "}
            to get set up.
          </>
        }
      />
    );
  }

  // 2FA verification required — show the code entry page
  if (mfaRequired) {
    return <VerifyMfaPage onVerified={() => window.location.reload()} />;
  }

  // User needs to join an org — show the join form with name collection
  if (pendingOrg) {
    return <JoinOrgPage pendingOrg={pendingOrg} onConfirm={handleJoinOrg} />;
  }

  // Auth error (e.g. no joinable orgs)
  if (authError) {
    const supportEmail = getSupportEmail(getBrandThemeFromDomain());
    return (
      <BrandedSplash
        title={authError}
        body={<>Please reach out to <a href={`mailto:${supportEmail}`} className="text-[#3B82F6] underline underline-offset-2">{supportEmail}</a> for assistance.</>}
      />
    );
  }

  // Not logged in — show the custom login page
  return <LoginPage />;
});

export default HomePage;
